/**
 * Back to Top Button
 *
 * Provides smooth scrolling back to top functionality with:
 * - Passive scroll listener for better performance
 * - Debounced scroll handler (~60fps)
 * - Cleanup function for dynamic sites (SPA, AJAX navigation)
 *
 * @since 1.0.0
 * @since 1.8.0 Added passive listener, debouncing, and cleanup mechanism.
 */

( function () {
	'use strict';

	// Configuration constants
	//
	// SCROLL_THRESHOLD (300px): User must scroll this distance before
	// the back-to-top button appears. 300px is approximately one viewport
	// height on mobile devices, ensuring the button only appears when
	// there's meaningful content above to scroll back to.
	const SCROLL_THRESHOLD = 300;

	// DEBOUNCE_DELAY (16ms): Matches ~60fps refresh rate (1000ms / 60 ≈ 16.67ms).
	// This limits scroll handler execution to prevent performance issues
	// while maintaining smooth visual updates.
	const DEBOUNCE_DELAY = 16;

	// Module state for cleanup
	let scrollHandler = null;
	let scrollTimeout = null;
	let buttonRef = null;
	let clickHandler = null;

	/**
	 * Initialize back to top functionality
	 */
	function initBackToTop() {
		const button = document.getElementById( 'back-to-top' );

		if ( ! button ) {
			return;
		}

		// Store reference for cleanup
		buttonRef = button;

		// Debounced scroll handler for better performance
		scrollHandler = function () {
			if ( scrollTimeout ) {
				return;
			}
			scrollTimeout = setTimeout( function () {
				scrollTimeout = null;
				// Check if button still exists and we haven't been cleaned up
				// (prevents errors if element removed or destroyBackToTop called)
				if ( buttonRef && document.body.contains( buttonRef ) ) {
					if ( window.pageYOffset >= SCROLL_THRESHOLD ) {
						buttonRef.classList.add( 'show' );
					} else {
						buttonRef.classList.remove( 'show' );
					}
				}
			}, DEBOUNCE_DELAY );
		};

		// Use passive listener for better scroll performance
		window.addEventListener( 'scroll', scrollHandler, { passive: true } );

		// Smooth scroll to top when clicked
		clickHandler = function ( e ) {
			e.preventDefault();
			scrollToTop();
		};
		buttonRef.addEventListener( 'click', clickHandler );

		// Initial check on page load
		if ( window.pageYOffset >= SCROLL_THRESHOLD ) {
			buttonRef.classList.add( 'show' );
		}
	}

	/**
	 * Smoothly scroll to the top of the page
	 */
	function scrollToTop() {
		const prefersReducedMotion =
			typeof window.matchMedia === 'function' &&
			window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		window.scrollTo( {
			top: 0,
			behavior: prefersReducedMotion ? 'auto' : 'smooth',
		} );
	}

	/**
	 * Cleanup function for dynamic sites (SPA, AJAX navigation)
	 *
	 * Call this before re-initializing or when component is unmounted.
	 * Prevents memory leaks by removing event listeners and clearing timers.
	 */
	function destroyBackToTop() {
		if ( scrollHandler ) {
			window.removeEventListener( 'scroll', scrollHandler );
			scrollHandler = null;
		}
		if ( scrollTimeout ) {
			clearTimeout( scrollTimeout );
			scrollTimeout = null;
		}
		if ( buttonRef && clickHandler ) {
			buttonRef.removeEventListener( 'click', clickHandler );
		}
		if ( buttonRef ) {
			buttonRef.classList.remove( 'show' );
			buttonRef = null;
		}
		clickHandler = null;
	}

	// Expose cleanup function globally for dynamic sites
	window.devwpBackToTop = {
		init: initBackToTop,
		destroy: destroyBackToTop,
	};

	document.addEventListener( 'DOMContentLoaded', initBackToTop );
} )();
